import { useEffect, useMemo } from "react";
import { useAppSelector, useAppDispatch } from "../../../redux/store";
import Avatar from "@mui/material/Avatar";
import { Alert, Box } from "@mui/material";

import {
  deleteUser,
  fetchUsers,
  selectUser,
  handleClearStatus,
} from "./usersSlice";

import UserDrawer from "./components/UserDrawer";
import Table from "../../../components/Table";
import UsersTableControls from "./components/TableControls";

const Users = () => {
  const dispatch = useAppDispatch();
  const { users, status, error, filters } = useAppSelector((state) => state.users);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchUsers());
    }
  }, [status, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(handleClearStatus());
    };
  }, [dispatch]);

  const handleDelete = (id: string | number) => {
    if (confirm("Are you sure you want to delete this user?")) {
      dispatch(deleteUser(id));
    }
  };

  const tableRows = useMemo(() => {
    const from = filters.createdFrom
      ? new Date(`${filters.createdFrom}T00:00:00`).getTime()
      : null;
    const to = filters.createdTo
      ? new Date(`${filters.createdTo}T23:59:59.999`).getTime()
      : null;

    return users
      .filter((user: any) => {
        if (from == null && to == null) return true;
        const created = Date.parse(user.timeCreated);
        if (!Number.isFinite(created)) return false;
        if (from != null && created < from) return false;
        if (to != null && created > to) return false;
        return true;
      })
      .map((user: any) => ({
        ...user,
        createdLabel: user.timeCreated
          ? new Date(user.timeCreated).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "—",
        profileImage: (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Avatar
              alt={user.fullName}
              src={user.photo}
              sx={{ width: 40, height: 40 }}
            />
          </Box>
        ),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, filters.createdFrom, filters.createdTo]);

  return (
    <>
      <UsersTableControls />
      {status === "failed" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as string) || "Failed to load users. Please try again."}
        </Alert>
      )}
      <Table
        enableColumnSearch
        headers={[
          {
            key: "profileImage",
            displayName: "Profile Image",
            disableColumnSearch: true,
          },
          { key: "fullName", displayName: "Name" },
          { key: "email", displayName: "Email" },
          { key: "position", displayName: "Position" },
          { key: "role", displayName: "Role" },
          { key: "status", displayName: "Status" },
          { key: "createdLabel", displayName: "Created" },
          { key: "origin", displayName: "Origin" },
        ]}
        // rows={users}
        rows={tableRows}
        actions={{
          edit: (id: string | number) => dispatch(selectUser(id)),
          delete: handleDelete,
        }}
        filters={{ role: filters.role, status: filters.status }}
        loading={status === "loading"}
      />
      <UserDrawer />
    </>
  );
};

export default Users;
